import React, { type ReactNode } from 'react';

import { useIntl } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Form, { ErrorMessage, Field, RequiredAsterisk } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import TeamsIcon from '@atlaskit/icon/core/teams';
import { CloseButton } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type User as UserPickerUser } from '@atlaskit/user-picker';
import { RecaptchaTermsAndPolicy } from '@atlassian/google-recaptcha';

import { type InvitedUser } from '../../types/user';
import { VOID_FUNC } from '../../utils/constants';
import { CreateContainers } from '../CreateContainer';
import { messages } from '../i18n';
import NoAccessErrorState from '../TeamCreateDialog/NoAccessErrorState';
import TeamTypePicker from '../TeamTypePicker';
import { TeamTypeSelect } from '../TeamTypeSelect/TeamTypeSelect';

import MemberPicker from './MemberPicker';
import { SuggestedTeamMembers } from './TeamMemberSuggestions';
import TeamNameInput from './TeamNameInput';
import { type FormProps } from './types';

const styles = cssMap({
	headerStyles: { marginBottom: token('space.150') },
	footerStyles: { marginTop: token('space.200') },
	recaptchaWrapperStyles: { marginTop: token('space.150') },
	fieldStyles: { gap: token('space.0') },
});

type InvitedUserWithSuggestion = InvitedUser & { isSuggestedMember?: boolean };
type UserPickerUserWithSuggestion = UserPickerUser & { isSuggestedMember?: boolean };

function EmptyWrapper({ children }: { children: ReactNode }) {
	return <>{children}</>;
}

function DefaultHeader({ children }: { children: ReactNode }) {
	return <Box xcss={styles.headerStyles}>{children}</Box>;
}

function DefaultFooter({ children }: { children: ReactNode }) {
	return (
		<Inline alignInline="end" space="space.100" xcss={styles.footerStyles}>
			{children}
		</Inline>
	);
}

function FormWrapper(props: FormProps): React.JSX.Element {
	const { formatMessage } = useIntl();

	const {
		handleSubmit,
		onClose = VOID_FUNC,
		isFetching,
		canCreateTeams,
		showTeamTypePicker,
		showTeamTypeSelect,
		onGoBack,
		teamNameInputProps,
		memberPickerProps,
		customInputFieldProps,
		teamTypePickerProps,
		teamTypeSelectProps,
		cloudId,
		currentUserId,
		customMessages = {},
		customComponents = {},
		showCreateContainers = false,
		createContainersProps,
		handleSuggestedMemberSelection,
		headless,
	} = props;

	const Header = customComponents?.header || customComponents?.modalHeader || DefaultHeader;
	const Title = customComponents?.title || customComponents?.modalTitle || EmptyWrapper;
	const Body = customComponents?.body || customComponents?.modalBody || EmptyWrapper;
	const Footer = customComponents?.footer || customComponents?.modalFooter || DefaultFooter;

	const customTitle = customMessages?.title || customMessages?.modalTitle;
	const submitButtonLabel =
		customMessages?.submitButtonLabel || formatMessage(messages.submitButtonLabelNext);

	if (!canCreateTeams) {
		return (
			<Body>
				<NoAccessErrorState onAction={onClose} />
			</Body>
		);
	}

	const platformisationFormsEnabled = fg('navx_uc_platformisation_forms_enabled');

	const isTeamMemberSuggestionsEnabled = FeatureGates.getExperimentValue(
		'team_member_suggestions_while_creating_team',
		'isEnabled',
		false,
	);

	const hideHeader = headless && platformisationFormsEnabled;

	return fg('platform-design_system_team-form_conversion') ? (
		<Form onSubmit={handleSubmit} noValidate>
			{!hideHeader && (
				<Header>
					<Title>
						{fg('enable_close_button_in_team_modals') ? (
							<Inline alignBlock="center" space="space.100" spread="space-between">
								<Inline alignBlock="center" space="space.100">
									{onGoBack ? (
										<IconButton
											spacing="compact"
											icon={(iconProps) => <ArrowLeftIcon {...iconProps} />}
											label={formatMessage(messages.backButton)}
											onClick={onGoBack}
										/>
									) : null}
									{customTitle ? (
										customTitle
									) : (
										<Inline alignBlock="baseline" space="space.050">
											<TeamsIcon label={formatMessage(messages.modalTitleIconLabel)} />
											<Heading as="h2" size="small">
												{formatMessage(messages.modalTitleNext)}
											</Heading>
										</Inline>
									)}
								</Inline>
								<CloseButton onClick={onClose} />
							</Inline>
						) : (
							<Inline alignBlock="center" space="space.100">
								{onGoBack ? (
									<IconButton
										spacing="compact"
										icon={(iconProps) => <ArrowLeftIcon {...iconProps} />}
										label={formatMessage(messages.backButton)}
										onClick={onGoBack}
									/>
								) : null}
								{customTitle ? (
									customTitle
								) : (
									<Inline alignBlock="baseline" space="space.050">
										<TeamsIcon label={formatMessage(messages.modalTitleIconLabel)} />
										<Heading as="h2" size="small">
											{formatMessage(messages.modalTitleNext)}
										</Heading>
									</Inline>
								)}
							</Inline>
						)}
					</Title>
				</Header>
			)}
			<Body>
				<Stack
					testId="create-team-content-dialog"
					space="space.100"
					xcss={platformisationFormsEnabled ? styles.fieldStyles : undefined}
				>
					{!hideHeader && (
						<Text size="small" color="color.text.subtlest">
							{formatMessage(messages.requiredFieldSummary)}
							<RequiredAsterisk />
						</Text>
					)}
					{customComponents.customInputField &&
						(() => {
							return <customComponents.customInputField {...customInputFieldProps()} />;
						})()}
					<TeamNameInput {...teamNameInputProps} />
					{customComponents.memberPicker ? (
						<customComponents.memberPicker {...memberPickerProps()} />
					) : (
						<Field<InvitedUser[]>
							name="inviteMembers"
							label={formatMessage(messages.invitationsFieldLabelNext)}
							id="inviteMembers"
							isRequired
							defaultValue={memberPickerProps().initialValues || []}
						>
							{({ fieldProps, error }) => {
								const { showErrorMessage, ...props } = memberPickerProps(error);
								const suggestedMembersCloudId = cloudId;
								const suggestedMembersAccountId = currentUserId;

								const handleSuggestedUserClick = (selectedUser: UserPickerUser) => {
									const currentValue = fieldProps.value || [];

									const isUserAlreadySelected = currentValue.some(
										(user) => user.id === selectedUser.id,
									);
									if (isUserAlreadySelected) {
										return;
									}

									const newInvitedUser: InvitedUserWithSuggestion = {
										id: selectedUser.id,
										email: selectedUser.email || '',
										fullName: selectedUser.name,
										nickname: selectedUser.publicName,
										avatarUrl: selectedUser.avatarUrl,
										isSuggestedMember: true,
									};

									const newValue = [...currentValue, newInvitedUser];

									if (handleSuggestedMemberSelection) {
										handleSuggestedMemberSelection(newInvitedUser);
									}

									fieldProps.onChange(newValue);

									if (props.onChange) {
										const newUserPickerUsers = newValue.map((user: InvitedUserWithSuggestion) => ({
											id: user.id,
											name: user.fullName,
											publicName: user.nickname,
											avatarUrl: user.avatarUrl,
											type: 'user' as const,
											email: user.email,
											isSuggestedMember: user.isSuggestedMember,
										}));
										props.onChange(newUserPickerUsers, { hasError: false, isDisabled: false });
									}
								};

								// Convert InvitedUser[] to UserPickerUser[] for SuggestedTeamMembers
								const userPickerUsers = (fieldProps.value || []).map((user) => ({
									id: user.id,
									name: user.fullName,
									publicName: user.nickname,
									avatarUrl: user.avatarUrl,
									type: 'user' as const,
									email: user.email,
								}));

								const initialUserPickerUsers = (props.initialValues || []).map((user) => ({
									id: user.id,
									name: user.fullName,
									publicName: user.nickname,
									avatarUrl: user.avatarUrl,
									type: 'user' as const,
									email: user.email,
								}));
								return (
									<>
										<MemberPicker
											{...fieldProps}
											{...props}
											value={fieldProps.value}
											ariaLabelledBy={`${fieldProps.id}-label`}
											ariaDescribedBy={`${fieldProps.id}-helper`}
											inputId={fieldProps.id}
											onChange={(users, meta) => {
												const invitedUsers = users.map((user: UserPickerUserWithSuggestion) => ({
													id: user.id,
													email: user.email || '',
													fullName: user.name,
													nickname: user.publicName,
													avatarUrl: user.avatarUrl,
													isSuggestedMember: user?.isSuggestedMember || false,
												}));
												fieldProps.onChange(invitedUsers);
												// Also call the original onChange from memberPickerProps
												if (props.onChange) {
													props.onChange(users, meta);
												}
											}}
											initialValues={props.initialValues}
										/>
										{isTeamMemberSuggestionsEnabled && (
											<SuggestedTeamMembers
												currentPickedUsers={userPickerUsers}
												onSuggestedUserClick={handleSuggestedUserClick}
												initialValues={initialUserPickerUsers}
												cloudId={suggestedMembersCloudId}
												accountId={suggestedMembersAccountId}
											/>
										)}
										{showErrorMessage ? (
											<ErrorMessage testId="invitation-field-empty-error">
												{formatMessage(messages.invitationsFieldErrorMessageNext)}
											</ErrorMessage>
										) : null}
									</>
								);
							}}
						</Field>
					)}
					{showTeamTypeSelect ? <TeamTypeSelect {...teamTypeSelectProps} /> : null}
					{showTeamTypePicker ? <TeamTypePicker {...teamTypePickerProps} /> : null}
					{showCreateContainers ? <CreateContainers {...createContainersProps} /> : null}
				</Stack>
				{memberPickerProps()?.allowEmailInvites ? (
					<Box xcss={styles.recaptchaWrapperStyles}>
						<Text size="small" color="color.text.subtle">
							<RecaptchaTermsAndPolicy />
						</Text>
					</Box>
				) : null}
			</Body>
			<Footer>
				<Button appearance={fg('jsm_ux_uplift') ? 'subtle' : 'default'} onClick={onClose}>
					{formatMessage(messages.cancelButtonLabel)}
				</Button>
				<Button appearance="primary" isLoading={isFetching} type="submit">
					{submitButtonLabel}
				</Button>
			</Footer>
		</Form>
	) : (
		<Form onSubmit={handleSubmit}>
			{({ formProps }) => (
				<form {...formProps} noValidate>
					{!hideHeader && (
						<Header>
							<Title>
								{fg('enable_close_button_in_team_modals') ? (
									<Inline alignBlock="center" space="space.100" spread="space-between">
										<Inline alignBlock="center" space="space.100">
											{onGoBack ? (
												<IconButton
													spacing="compact"
													icon={(iconProps) => <ArrowLeftIcon {...iconProps} />}
													label={formatMessage(messages.backButton)}
													onClick={onGoBack}
												/>
											) : null}
											{customTitle ? (
												customTitle
											) : (
												<Inline alignBlock="baseline" space="space.050">
													<TeamsIcon label={formatMessage(messages.modalTitleIconLabel)} />
													<Heading as="h2" size="small">
														{formatMessage(messages.modalTitleNext)}
													</Heading>
												</Inline>
											)}
										</Inline>
										<CloseButton onClick={onClose} />
									</Inline>
								) : (
									<Inline alignBlock="center" space="space.100">
										{onGoBack ? (
											<IconButton
												spacing="compact"
												icon={(iconProps) => <ArrowLeftIcon {...iconProps} />}
												label={formatMessage(messages.backButton)}
												onClick={onGoBack}
											/>
										) : null}
										{customTitle ? (
											customTitle
										) : (
											<Inline alignBlock="baseline" space="space.050">
												<TeamsIcon label={formatMessage(messages.modalTitleIconLabel)} />
												<Heading as="h2" size="small">
													{formatMessage(messages.modalTitleNext)}
												</Heading>
											</Inline>
										)}
									</Inline>
								)}
							</Title>
						</Header>
					)}
					<Body>
						<Stack
							testId="create-team-content-dialog"
							space="space.100"
							xcss={platformisationFormsEnabled ? styles.fieldStyles : undefined}
						>
							{!hideHeader && (
								<Text size="small" color="color.text.subtlest">
									{formatMessage(messages.requiredFieldSummary)}
									<RequiredAsterisk />
								</Text>
							)}
							{customComponents.customInputField &&
								(() => {
									return <customComponents.customInputField {...customInputFieldProps()} />;
								})()}
							<TeamNameInput {...teamNameInputProps} />
							{customComponents.memberPicker ? (
								<customComponents.memberPicker {...memberPickerProps()} />
							) : (
								<Field<InvitedUser[]>
									name="inviteMembers"
									label={formatMessage(messages.invitationsFieldLabelNext)}
									id="inviteMembers"
									isRequired
									defaultValue={memberPickerProps().initialValues || []}
								>
									{({ fieldProps, error }) => {
										const { showErrorMessage, ...props } = memberPickerProps(error);
										const suggestedMembersCloudId = cloudId;
										const suggestedMembersAccountId = currentUserId;

										// Handle suggested user selection
										const handleSuggestedUserClick = (selectedUser: UserPickerUser) => {
											const currentValue = fieldProps.value || [];

											// Check if user is already selected
											const isUserAlreadySelected = currentValue.some(
												(user) => user.id === selectedUser.id,
											);
											if (isUserAlreadySelected) {
												return;
											}

											// Create new invited user
											const newInvitedUser: InvitedUserWithSuggestion = {
												id: selectedUser.id,
												email: selectedUser.email || '',
												fullName: selectedUser.name,
												nickname: selectedUser.publicName,
												avatarUrl: selectedUser.avatarUrl,
												isSuggestedMember: true,
											};

											// Merge with existing selection
											const newValue = [...currentValue, newInvitedUser];

											if (handleSuggestedMemberSelection) {
												handleSuggestedMemberSelection(newInvitedUser);
											}

											fieldProps.onChange(newValue);

											if (props.onChange) {
												const newUserPickerUsers = newValue.map((user: InvitedUserWithSuggestion) => ({
													id: user.id,
													name: user.fullName,
													publicName: user.nickname,
													avatarUrl: user.avatarUrl,
													type: 'user' as const,
													email: user.email,
													isSuggestedMember: user.isSuggestedMember,
												}));
												props.onChange(newUserPickerUsers, { hasError: false, isDisabled: false });
											}
										};

										// Convert InvitedUser[] to UserPickerUser[] for SuggestedTeamMembers
										const userPickerUsers = (fieldProps.value || []).map((user) => ({
											id: user.id,
											name: user.fullName,
											publicName: user.nickname,
											avatarUrl: user.avatarUrl,
											type: 'user' as const,
											email: user.email,
										}));

										// Convert initialValues to UserPickerUser[]
										const initialUserPickerUsers = (props.initialValues || []).map((user) => ({
											id: user.id,
											name: user.fullName,
											publicName: user.nickname,
											avatarUrl: user.avatarUrl,
											type: 'user' as const,
											email: user.email,
										}));
										return (
											<>
												<MemberPicker
													{...fieldProps}
													{...props}
													value={fieldProps.value}
													ariaLabelledBy={`${fieldProps.id}-label`}
													ariaDescribedBy={`${fieldProps.id}-helper`}
													inputId={fieldProps.id}
													onChange={(users, meta) => {
														const invitedUsers = users.map((user: UserPickerUserWithSuggestion) => ({
															id: user.id,
															email: user.email || '',
															fullName: user.name,
															nickname: user.publicName,
															avatarUrl: user.avatarUrl,
															isSuggestedMember: user?.isSuggestedMember || false,
														}));
														fieldProps.onChange(invitedUsers);
														// Also call the original onChange from memberPickerProps
														if (props.onChange) {
															props.onChange(users, meta);
														}
													}}
													initialValues={props.initialValues}
												/>
												{isTeamMemberSuggestionsEnabled && (
													<SuggestedTeamMembers
														currentPickedUsers={userPickerUsers}
														onSuggestedUserClick={handleSuggestedUserClick}
														initialValues={initialUserPickerUsers}
														cloudId={suggestedMembersCloudId}
														accountId={suggestedMembersAccountId}
													/>
												)}
												{showErrorMessage ? (
													<ErrorMessage testId="invitation-field-empty-error">
														{formatMessage(messages.invitationsFieldErrorMessageNext)}
													</ErrorMessage>
												) : null}
											</>
										);
									}}
								</Field>
							)}
							{showTeamTypeSelect ? <TeamTypeSelect {...teamTypeSelectProps} /> : null}
							{showTeamTypePicker ? <TeamTypePicker {...teamTypePickerProps} /> : null}
							{showCreateContainers ? <CreateContainers {...createContainersProps} /> : null}
						</Stack>
						{memberPickerProps()?.allowEmailInvites ? (
							<Box xcss={styles.recaptchaWrapperStyles}>
								<Text size="small" color="color.text.subtle">
									<RecaptchaTermsAndPolicy />
								</Text>
							</Box>
						) : null}
					</Body>
					<Footer>
						<Button appearance={fg('jsm_ux_uplift') ? 'subtle' : 'default'} onClick={onClose}>
							{formatMessage(messages.cancelButtonLabel)}
						</Button>
						<Button appearance="primary" isLoading={isFetching} type="submit">
							{submitButtonLabel}
						</Button>
					</Footer>
				</form>
			)}
		</Form>
	);
}

export default FormWrapper;
